import { Container, Typography, Box, Link, Card, CardContent } from '@mui/material';
import Grid from '@mui/material/Grid2';

function BalancingIndeterminacy() {
    return (
        <Container sx={{ padding: '40px 0' }}>
            <Typography variant="h2" gutterBottom sx={{ textAlign: 'left', marginBottom: '24px' }}>
                Balancing Indeterminacy and Structure: Neural Text Generation for Artistic Inspiration
            </Typography>

            <Typography variant="body1" sx={{ fontSize: '1.1rem', marginBottom: '30px', fontStyle: 'italic' }}>
                Published at the 14th International Conference on Artificial Intelligence in Music, Sound, Art and Design (EvoMusArt 2025)
            </Typography>

            {/* Abstract Section */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    Abstract
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6, textAlign: 'justify' }}>
                    This paper explores the potential of neural text generative models to produce poetic lines that can serve as seeds for artistic inspiration. Drawing on theories of aesthetic perception and creativity, we hypothesize that lines characterized by indeterminacy and occupying an intermediate space between randomness and predictability are most effective at inspiring creative work. We compare two architectures: Long Short-Term Memory Variational Autoencoders (LSTM-VAE) and Transformer-based Large Language Models (LLMs), analyzing their outputs using metrics that capture linguistic, stylistic, and poetic qualities. Our analysis reveals that LSTM-VAE generates lines with higher global entropy and more variable syntactic patterns while maintaining lower levels of pretentiousness compared to LLMs. While LLM-generated lines demonstrate richer conventional poetic imagery, they often present overly finished forms that may inhibit rather than stimulate creative exploration. The study's findings suggest that smaller, more focused models trained on curated datasets might be more effective at generating semantically open lines than larger, general-purpose language models.
                </Typography>
            </Box>

            {/* Key Contributions Section */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    Key Contributions
                </Typography>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    Theoretical Framework
                                </Typography>
                                <Typography variant="body2">
                                    We establish a foundation for understanding how generative models can contribute to the Seed phase of creativity by exploring the role of randomness, predictability, indeterminacy, and estrangement in art and literature.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    Architectural Comparison
                                </Typography>
                                <Typography variant="body2">
                                    We compare LSTM-VAE and Transformer-based LLM architectures for text generation, examining their suitability for producing lines that occupy the productive mid-range between predictability and randomness.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    Comprehensive Analysis
                                </Typography>
                                <Typography variant="body2">
                                    We perform comparative analysis using automatic metrics measuring linguistic and stylistic properties, including entropy, surprisal, abstractedness, valency, syntactic complexity, and indeterminacy.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* Research Insights Section */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    Research Insights
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6, marginBottom: '20px' }}>
                    Our analysis reveals fundamental differences in how LSTM-VAE and LLM systems generate text for creative inspiration:
                </Typography>

                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Card sx={{ height: '100%', backgroundColor: '#f8f9fa' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
                                    LSTM-VAE Characteristics
                                </Typography>
                                <Typography variant="body2" sx={{ marginBottom: '10px' }}>
                                    <strong>Higher Global Entropy:</strong> Creates more variable and unpredictable overall linguistic structures
                                </Typography>
                                <Typography variant="body2" sx={{ marginBottom: '10px' }}>
                                    <strong>Syntactic Indeterminacy:</strong> Generates incomplete or unconventional syntactic structures that invite interpretation
                                </Typography>
                                <Typography variant="body2" sx={{ marginBottom: '10px' }}>
                                    <strong>Lower Pretentiousness:</strong> Maintains authenticity while avoiding overwrought metaphors
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Semantic Openness:</strong> Balances concrete imagery with abstract concepts, creating spaces for multiple interpretations
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Card sx={{ height: '100%', backgroundColor: '#f8f9fa' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                                    LLM Characteristics
                                </Typography>
                                <Typography variant="body2" sx={{ marginBottom: '10px' }}>
                                    <strong>Higher Surprisal:</strong> Produces less predictable individual word sequences
                                </Typography>
                                <Typography variant="body2" sx={{ marginBottom: '10px' }}>
                                    <strong>Conventional Poetic Devices:</strong> Employs traditional metaphorical patterns and complete syntactic structures
                                </Typography>
                                <Typography variant="body2" sx={{ marginBottom: '10px' }}>
                                    <strong>Rich Imagery:</strong> Demonstrates sophisticated poetic imagery but may be overly finished
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Thematic Coherence:</strong> Maintains consistent themes that may constrain creative exploration
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* Slides Section */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    Presentation Slides
                </Typography>
                <Typography variant="body1" sx={{ marginBottom: '20px' }}>
                    View the presentation slides from EvoMusArt 2025:
                </Typography>
                <Box sx={{
                    width: '100%',
                    height: '600px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    overflow: 'hidden'
                }}>
                    <iframe
                        src="/pdf/Balancing-indeterminacy-slides.pdf"
                        style={{
                            width: '100%',
                            height: '100%',
                            border: 'none'
                        }}
                        title="Balancing Indeterminacy and Structure Slides"
                    />
                </Box>
            </Box>

            {/* Paper Section */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    Research Paper
                </Typography>
                <Typography variant="body1" sx={{ marginBottom: '20px' }}>
                    Read the full research paper:
                </Typography>
                <Box sx={{
                    width: '100%',
                    height: '800px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    overflow: 'hidden'
                }}>
                    <iframe
                        src="/pdf/Balancing.pdf"
                        style={{
                            width: '100%',
                            height: '100%',
                            border: 'none'
                        }}
                        title="Balancing Indeterminacy and Structure Paper"
                    />
                </Box>
            </Box>

            {/* Links Section */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    Resources
                </Typography>
                <Grid container spacing={2}>
                   <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Link
                            href="/pdf/balancing-indeterminacy-slides.pdf"
                            target="_blank"
                            rel="noopener"
                            sx={{ display: 'block', padding: '10px', textDecoration: 'none' }}
                        >
                            <Card sx={{ textAlign: 'center', '&:hover': { backgroundColor: '#f5f5f5' } }}>
                                <CardContent>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                        Download Slides
                                    </Typography>
                                    <Typography variant="body2">
                                        Slides from EvoMusArt 2025
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Link>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Link
                            href="/pdf/Balancing.pdf"
                            target="_blank"
                            rel="noopener"
                            sx={{ display: 'block', padding: '10px', textDecoration: 'none' }}
                        >
                            <Card sx={{ textAlign: 'center', '&:hover': { backgroundColor: '#f5f5f5' } }}>
                                <CardContent>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                        Download Paper
                                    </Typography>
                                    <Typography variant="body2">
                                        PDF version of the research paper
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Link>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Link
                            href="https://sites.google.com/view/balancing-indeterminacy/paper"
                            target="_blank"
                            rel="noopener"
                            sx={{ display: 'block', padding: '10px', textDecoration: 'none' }}
                        >
                            <Card sx={{ textAlign: 'center', '&:hover': { backgroundColor: '#f5f5f5' } }}>
                                <CardContent>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                        Supplementary Materials
                                    </Typography>
                                    <Typography variant="body2">
                                        Additional examples and prompts
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Link>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Link
                            href="https://link.springer.com/chapter/10.1007/978-3-031-90167-6_14"
                            target="_blank"
                            rel="noopener"
                            sx={{ display: 'block', padding: '10px', textDecoration: 'none' }}
                        >
                            <Card sx={{ textAlign: 'center', '&:hover': { backgroundColor: '#f5f5f5' } }}>
                                <CardContent>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                        Conference Paper
                                    </Typography>
                                    <Typography variant="body2">
                                        EvoMusArt 2025 proceedings
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Link>
                    </Grid>
                </Grid>
            </Box>

            {/* Examples Section */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    Example Generated Lines
                </Typography>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
                                    LSTM-VAE Generated Lines
                                </Typography>
                                <Typography variant="body2" sx={{ fontStyle: 'italic', marginBottom: '10px' }}>
                                    "beneath the longing"
                                </Typography>
                                <Typography variant="body2" sx={{ fontStyle: 'italic', marginBottom: '10px' }}>
                                    "we're lost and happiness in the way"
                                </Typography>
                                <Typography variant="body2" sx={{ fontStyle: 'italic', marginBottom: '10px' }}>
                                    "i feel the universe of the world steel"
                                </Typography>
                                <Typography variant="body2" sx={{ fontStyle: 'italic', marginBottom: '10px' }}>
                                    "through the dream and can not see"
                                </Typography>
                                <Typography variant="body2" sx={{ marginTop: '15px' }}>
                                    These lines demonstrate syntactic indeterminacy and semantic openness, creating spaces for interpretation.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                                    LLM Generated Lines
                                </Typography>
                                <Typography variant="body2" sx={{ fontStyle: 'italic', marginBottom: '10px' }}>
                                    "through the mist, a voice remains"
                                </Typography>
                                <Typography variant="body2" sx={{ fontStyle: 'italic', marginBottom: '10px' }}>
                                    "love writes its lies on the bones of fear"
                                </Typography>
                                <Typography variant="body2" sx={{ fontStyle: 'italic', marginBottom: '10px' }}>
                                    "in silence, i become the void"
                                </Typography>
                                <Typography variant="body2" sx={{ fontStyle: 'italic', marginBottom: '10px' }}>
                                    "moonlight drips from broken clouds"
                                </Typography>
                                <Typography variant="body2" sx={{ marginTop: '15px' }}>
                                    These lines show conventional poetic devices and complete syntactic structures with rich imagery.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* Citation Section */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    Citation
                </Typography>
                <Box sx={{
                    backgroundColor: '#f5f5f5',
                    padding: '20px',
                    borderRadius: '4px',
                    fontFamily: 'monospace',
                    fontSize: '0.9rem'
                }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        @inproceedings{`{`}vechtomova2025balancing,<br />
                        &nbsp;&nbsp;title={`{`}Balancing Indeterminacy and Structure: Neural Text Generation for Artistic Inspiration{`}`},<br />
                        &nbsp;&nbsp;author={`{`}Vechtomova, Olga and Sahu, Gaurav{`}`},<br />
                        &nbsp;&nbsp;booktitle={`{`}Artificial Intelligence in Music, Sound, Art and Design{`}`},<br />
                        &nbsp;&nbsp;pages={`{`}202--217{`}`},<br />
                        &nbsp;&nbsp;year={`{`}2025{`}`},<br />
                        &nbsp;&nbsp;publisher={`{`}Springer Nature Switzerland{`}`}<br />
                        {`}`}
                    </Typography>
                </Box>
            </Box>
        </Container>
    );
}

export default BalancingIndeterminacy;